import axios from 'axios';
import {
  getAccessToken, refrescarSesion, terminarSesion, setOnSessionEnded, SesionTerminada,
} from './session';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// Interceptor de request: agrega el token JWT en cada llamada autenticada.
// Pisa también los Authorization que algunas pantallas ponen a mano, para que
// el reintento después de un refresh salga con el token nuevo.
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Fin de sesión ───────────────────────────────────────────────────────────
// Antes: cualquier 401 → borrar token + window.location.href = '/'. Esa
// recarga destruía todo el estado de React (un formulario completo, por
// ejemplo) sin decir nada. Ahora terminarSesion() avisa a AuthContext, que
// muestra un modal de re-login sobre la pantalla actual sin desmontarla.

export { setOnSessionEnded };

const MSJ_SESION_TERMINADA = 'Tu sesión se cerró. Ingresá de nuevo para continuar.';
const MSJ_SIN_RED          = 'No pudimos renovar tu sesión. Revisá tu conexión e intentá de nuevo.';

// Las pantallas muestran err.response.data.error: que diga la causa real y
// no "Token expirado".
function conMensaje(error, mensaje) {
  if (error.response) {
    error.response.data = { ...(error.response.data || {}), error: mensaje };
  }
  return error;
}

// Un 401 de estas rutas es la respuesta en sí (credenciales incorrectas,
// refresh rechazado), no una sesión vencida.
const RUTAS_DE_AUTH = ['/auth/login', '/auth/refresh', '/auth/logout'];

// Interceptor de response — clasifica los 401 (antes: todos cerraban sesión).
//   TOKEN_EXPIRED            → refresh + un reintento
//   TOKEN_INVALID            → cierra sesión
//   refresh rechazado (401)  → cierra sesión
//   refresh caído (red/5xx)  → error a la pantalla, la sesión sigue
//   401 sin código           → error a la pantalla (p. ej. "La contraseña
//                              actual es incorrecta"); sólo cierra si ya no
//                              queda ningún token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    if (!config || response?.status !== 401) return Promise.reject(error);
    if (RUTAS_DE_AUTH.some((r) => config.url?.startsWith(r))) return Promise.reject(error);

    const code = response.data?.code;

    if (code === 'TOKEN_EXPIRED' && !config._reintentado) {
      const tokenUsado = String(config.headers?.Authorization || '').replace(/^Bearer /, '') || null;
      try {
        const nuevo = await refrescarSesion(tokenUsado);
        config._reintentado = true;
        config.headers.Authorization = `Bearer ${nuevo}`;
        return api(config);
      } catch (e) {
        if (e instanceof SesionTerminada) {
          terminarSesion(e.motivo);
          return Promise.reject(conMensaje(error, MSJ_SESION_TERMINADA));
        }
        return Promise.reject(conMensaje(error, MSJ_SIN_RED));
      }
    }

    if (code === 'TOKEN_INVALID' || code === 'TOKEN_EXPIRED' || !getAccessToken()) {
      terminarSesion(code || 'sin_token');
      return Promise.reject(conMensaje(error, MSJ_SESION_TERMINADA));
    }

    return Promise.reject(error);
  }
);

// Gancho para las pruebas end-to-end locales: sólo existe si el build se
// hace con REACT_APP_E2E=true (CRA lo resuelve en build y lo elimina si no).
if (process.env.REACT_APP_E2E === 'true') window.__fixoApi = api;

export default api;
